/* ============================================
   CRC PRODUCT PAGE ENHANCEMENTS
   - Dynamic header height via ResizeObserver (load + resize only, NOT scroll)
   - Dynamic sticky cart height tracking (load + resize only, NOT scroll)
   - Active image aspect-ratio detection
   - Hover zoom on active slide only (instant mouse tracking via rAF)
   - Thumbnail swap via setActiveMedia
   - Lightbox suppression via modal.show() patch
   ============================================ */

(function() {
  'use strict';

  const desktopQuery = window.matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)');

  // ---------- HEADER HEIGHT ----------
  // Measured on load + resize only
  // NOT on scroll - header shrinks on scroll which would grow the image

  let rafId = null;
  let headerObserver = null;

  function getHeaderHeight() {
    const header = document.querySelector('.header-wrapper');
    if (!header) return 78;
    return Math.ceil(header.getBoundingClientRect().height || header.offsetHeight || 78);
  }

  function writeHeaderHeight() {
    cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(function() {
      const height = getHeaderHeight();
      if (height > 0) {
        document.documentElement.style.setProperty('--crc-header-height', height + 'px');
      }
    });
  }

  function observeHeader() {
    const header = document.querySelector('.header-wrapper');
    if (!header) return;
    if (headerObserver) headerObserver.disconnect();
    headerObserver = new ResizeObserver(writeHeaderHeight);
    headerObserver.observe(header);
    writeHeaderHeight();
  }

  observeHeader();
  window.addEventListener('resize', writeHeaderHeight, { passive: true });
  window.addEventListener('orientationchange', writeHeaderHeight, { passive: true });
  document.addEventListener('shopify:section:load', observeHeader);

  // ---------- STICKY CART HEIGHT ----------
  // Measured on load + resize only
  // NOT on scroll - pill disappears on scroll which would grow the image

  function updateStickyCartHeight() {
    const pill = document.querySelector('.satc-pill');
    if (!pill) {
      document.documentElement.style.setProperty('--crc-bottom-pill-space', '0px');
      return;
    }
    const styles = window.getComputedStyle(pill);
    const visible = pill.classList.contains('is-visible')
                    && styles.display !== 'none'
                    && styles.visibility !== 'hidden';
    if (!visible) {
      document.documentElement.style.setProperty('--crc-bottom-pill-space', '112px');
      return;
    }
    const rect = pill.getBoundingClientRect();
    const bottomOffset = parseInt(styles.bottom) || 22;
    const total = rect.height + bottomOffset + 14;
    document.documentElement.style.setProperty('--crc-bottom-pill-space', total + 'px');
  }

  function syncPillHeight() {
    updateStickyCartHeight();
    const gallery = document.querySelector('media-gallery');
    if (gallery) {
      const val = getComputedStyle(document.documentElement)
        .getPropertyValue('--crc-bottom-pill-space').trim();
      if (val) gallery.style.setProperty('--crc-bottom-pill-space', val);
    }
  }

  // Measure after short delay to let pill render
  setTimeout(syncPillHeight, 500);
  window.addEventListener('resize', syncPillHeight, { passive: true });
  if (window.ResizeObserver) {
    new ResizeObserver(syncPillHeight).observe(document.body);
  }

  // ---------- PATCH PRODUCT MODAL ----------

  function patchProductModal() {
    document.querySelectorAll('product-modal, modal-dialog').forEach(function(modal) {
      if (modal.dataset.crcModalPatched === 'true') return;
      if (typeof modal.show !== 'function') return;
      modal.dataset.crcModalPatched = 'true';

      const originalShow = modal.show.bind(modal);
      modal.show = function(opener) {
        if (desktopQuery.matches) {
          if (typeof this.hide === 'function') this.hide();
          return;
        }
        return originalShow(opener);
      };
    });
  }

  patchProductModal();
  document.addEventListener('shopify:section:load', patchProductModal);

  // ---------- THUMBNAIL SWAP ----------

  function setupThumbnailSwap() {
    const gallery = document.querySelector('media-gallery');
    if (!gallery || typeof gallery.setActiveMedia !== 'function') return;

    const thumbList = gallery.querySelector('.thumbnail-list');
    if (!thumbList || thumbList.dataset.crcSwapBound) return;
    thumbList.dataset.crcSwapBound = 'true';

    thumbList.addEventListener('click', function(e) {
      if (!desktopQuery.matches) return;
      const item = e.target.closest('.thumbnail-list__item');
      if (!item || !item.dataset.target) return;

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      gallery.setActiveMedia(item.dataset.target, false);

      if (typeof gallery.setActiveThumbnail === 'function') {
        const btn = item.querySelector('button.thumbnail');
        if (btn) gallery.setActiveThumbnail(btn);
      }
    }, true);
  }

  // ---------- HOVER ZOOM ----------

  function getBestImageUrl(img) {
    if (!img) return null;
    const srcset = img.getAttribute('srcset');
    if (srcset) {
      const candidates = srcset.split(',').map(function(item) {
        const parts = item.trim().split(/\s+/);
        return {
          url: parts[0],
          width: parts[1] ? parseInt(parts[1].replace('w', ''), 10) : 0
        };
      }).filter(function(i) { return i.url; })
        .sort(function(a, b) { return b.width - a.width; });
      if (candidates[0]) return candidates[0].url;
    }
    return img.currentSrc || img.src || null;
  }

  function updateActiveZoom(gallery) {
    if (!desktopQuery.matches) return;

    gallery.querySelectorAll('.product__media-item').forEach(function(item) {
      const container = item.querySelector('.product-media-container');
      if (!container) return;

      if (!item.classList.contains('is-active')) {
        container.removeAttribute('data-crc-zoom-ready');
        container.style.removeProperty('--crc-zoom-image');
        container.style.removeProperty('--crc-active-media-aspect-ratio');
        container.classList.remove('crc-zoom-active');
        return;
      }

      const img = item.querySelector('img');
      const imageUrl = getBestImageUrl(img);
      if (!imageUrl) return;

      container.style.setProperty('--crc-zoom-image', 'url("' + imageUrl + '")');
      container.setAttribute('data-crc-zoom-ready', 'true');

      if (img && img.naturalWidth && img.naturalHeight) {
        container.style.setProperty(
          '--crc-active-media-aspect-ratio',
          img.naturalWidth + ' / ' + img.naturalHeight
        );
      }
    });
  }

  function bindZoomMovement(gallery) {
    if (gallery.dataset.crcZoomMovementBound) return;
    gallery.dataset.crcZoomMovementBound = 'true';

    let zoomRafId = null;

    gallery.addEventListener('mouseenter', function(e) {
      if (!desktopQuery.matches) return;
      const container = e.target.closest(
        '.product__media-item.is-active .product-media-container[data-crc-zoom-ready="true"]'
      );
      if (container) container.classList.add('crc-zoom-active');
    }, true);

    gallery.addEventListener('mousemove', function(e) {
      if (!desktopQuery.matches) return;
      const container = e.target.closest(
        '.product__media-item.is-active .product-media-container[data-crc-zoom-ready="true"]'
      );
      if (!container) return;

      container.classList.add('crc-zoom-active');

      const rect = container.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width * 100);
      const y = ((e.clientY - rect.top) / rect.height * 100);

      if (zoomRafId) cancelAnimationFrame(zoomRafId);
      zoomRafId = requestAnimationFrame(function() {
        container.style.setProperty('--crc-zoom-x', x + '%');
        container.style.setProperty('--crc-zoom-y', y + '%');
      });
    });

    gallery.addEventListener('mouseleave', function() {
      if (zoomRafId) cancelAnimationFrame(zoomRafId);
      gallery.querySelectorAll('.product-media-container').forEach(function(c) {
        c.classList.remove('crc-zoom-active');
      });
    });
  }

  function initGallery(gallery) {
    const items = gallery.querySelectorAll('.product__media-item');
    const observer = new MutationObserver(function(mutations) {
      const activeChanged = mutations.some(function(m) {
        return m.type === 'attributes' &&
               m.attributeName === 'class' &&
               m.target.classList.contains('product__media-item');
      });
      if (activeChanged) {
        requestAnimationFrame(function() { updateActiveZoom(gallery); });
      }
    });

    items.forEach(function(item) {
      observer.observe(item, { attributes: true, attributeFilter: ['class'] });
    });

    bindZoomMovement(gallery);
    updateActiveZoom(gallery);

    desktopQuery.addEventListener('change', function() { updateActiveZoom(gallery); });
  }

  // ---------- INIT ----------

  function init() {
    patchProductModal();
    setupThumbnailSwap();
    document.querySelectorAll('media-gallery').forEach(initGallery);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('variant:change', init);
  document.addEventListener('shopify:section:load', init);

})();


/* ============================================
   CRC KLARNA OSM DARK THEME (reskin)
   Klarna's on-site messaging renders its text inside a shadow root,
   themed by the data-theme attribute on <klarna-placement>. The app
   embed hardcodes data-theme="light", which paints light text for a
   light background = invisible on the dark reskin. CSS can't cross the
   shadow boundary, so flip the attribute to "dark" (Klarna re-renders
   itself) and re-apply whenever Klarna mounts / re-renders a placement.
   Scope: only ever sets data-theme on klarna-placement elements.
   TO REVERT: delete this block.
   ============================================ */
(function() {
  'use strict';
  function darkenKlarna() {
    var nodes = document.querySelectorAll('klarna-placement');
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].getAttribute('data-theme') !== 'dark') {
        nodes[i].setAttribute('data-theme', 'dark');
      }
    }
  }
  darkenKlarna();
  var mo = new MutationObserver(function() { darkenKlarna(); });
  mo.observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['data-theme']
  });
  window.addEventListener('load', darkenKlarna);
  document.addEventListener('shopify:section:load', darkenKlarna);
  for (var t = 0; t < 20; t++) { setTimeout(darkenKlarna, t * 300); }
})();


/* ============================================
   CRC AFTERSHIP REVIEWS — DARK + COMPACT (reskin)
   The AfterShip / Automizely reviews widget renders inside an (open)
   shadow root: host #am-reviews-carousel-shadow-container-production.
   Every colour is hardcoded as an INLINE style on the nodes, so neither
   the theme CSS nor the reskin passes can reach or override it. Fix:
   inject a dark stylesheet INTO the shadow root (external !important
   beats inline non-important), and cap + centre the host width in the
   light DOM to compact the sprawling full-bleed layout.
   Class names are React-hashed (e.g. _cardContainer--ZKekts3b), so we
   match on the stable prefix via [class*='_cardContainer']. Re-applied
   on mutation + timed retries because the widget re-renders itself.
   Stars keep their amber fill; empty stars + dates are muted.
   Scope: only the reviews carousel shadow host. TO REVERT: delete this.
   ============================================ */
(function() {
  'use strict';
  var CSS = [
    "*{ color:#F5F1E8 !important; }",
    "div[style*='background-color: rgb(255, 255, 255)']{ background-color:#11100E !important; }",
    "[class*='_cardContainer']{ background-color:#15140F !important; color:#F5F1E8 !important; border:1px solid rgba(159,133,97,.22) !important; border-radius:10px !important; padding:12px 16px !important; margin:0 0 10px !important; }",
    "[class*='_theButton']{ background-color:#9F8561 !important; color:#1A1814 !important; border:none !important; border-radius:0 !important; -webkit-clip-path:polygon(2.5% 0, 100% 0, 97.5% 100%, 0 100%) !important; clip-path:polygon(2.5% 0, 100% 0, 97.5% 100%, 0 100%) !important; text-transform:uppercase !important; letter-spacing:.08em !important; font-weight:700 !important; }",
    "[class*='_progressBar']{ background-color:rgba(245,241,232,.14) !important; }",
    "[class*='_progressBar'] > span{ background-color:#9F8561 !important; }",
    ".solid-select-control{ background-color:#1b1915 !important; border:1px solid rgba(159,133,97,.4) !important; }",
    ".solid-select-single-value,.solid-select-input{ color:#F5F1E8 !important; }",
    ".solid-select-right-box svg,.solid-select-right-box path{ color:#F5F1E8 !important; fill:#F5F1E8 !important; }",
    "[style*='color: rgb(99, 115, 129)'],[style*='color:#637381']{ color:rgba(245,241,232,.55) !important; }",
    "rect[style*='#dfe3e8']{ fill:rgba(245,241,232,.18) !important; }",
    "div[style*='height: 180px']{ height:auto !important; }",
    "/* --- dropdown panel + options (open sort menu) --- */",
    ".solid-select-list{ background-color:#1b1915 !important; border:1px solid rgba(159,133,97,.4) !important; box-shadow:0 14px 30px rgba(0,0,0,.55) !important; border-radius:8px !important; padding:4px !important; }",
    ".solid-select-list *{ color:#F5F1E8 !important; }",
    ".solid-select-option{ color:#F5F1E8 !important; background-color:transparent !important; border-radius:6px !important; }",
    ".solid-select-option:hover,.solid-select-option[data-focused='true'],.solid-select-option.solid-select-option--focused{ background-color:rgba(159,133,97,.20) !important; color:#F5F1E8 !important; }",
    "/* --- Sort overlap fix: the label + select sit in a bare display:flex with no gap. Target the real label class + give it spacing and vertical centring. --- */",
    "[class*='_filterSortContainer']{ align-items:center !important; }",
    "[class*='_filterSortContainer'] > div[style*='margin-left'] > div{ display:flex !important; align-items:center !important; gap:10px !important; }",
    "label[class*='_sort']{ margin-right:10px !important; white-space:nowrap !important; line-height:1 !important; }"
  ].join('\n');

  function inject(root) {
    if (!root) return;
    var s = root.querySelector('style[data-crc-reviews]');
    if (!s) {
      s = document.createElement('style');
      s.setAttribute('data-crc-reviews', '1');
      root.appendChild(s);
    }
    if (s.textContent !== CSS) s.textContent = CSS;
  }

  function apply() {
    var hosts = document.querySelectorAll('[id*=am-reviews-carousel]');
    for (var i = 0; i < hosts.length; i++) {
      var h = hosts[i];
      if (h.shadowRoot) {
        inject(h.shadowRoot);
        h.style.maxWidth = '1100px';
        h.style.marginLeft = 'auto';
        h.style.marginRight = 'auto';
        h.style.display = 'block';
      }
    }
  }

  apply();
  var mo = new MutationObserver(function() { apply(); });
  mo.observe(document.documentElement, { childList: true, subtree: true });
  window.addEventListener('load', apply);
  document.addEventListener('shopify:section:load', apply);
  for (var t = 0; t < 25; t++) { setTimeout(apply, t * 300); }
})();

/* ─────────────────────────────────────────────────────────────────────────
   CRC: hide the Coax chat bubble on the PRODUCT page while it would sit on
   top of the Shop Pay button. The bubble is fixed bottom-right; when the user
   scrolls so the Shop Pay (dynamic checkout) button enters the bottom band of
   the viewport, hide the bubble; show it again once the button scrolls away.
   Toggles body.crc-hide-coax (the actual hide rule lives in crc-product.css).
   ───────────────────────────────────────────────────────────────────────── */
(function () {
  if (!document.body.classList.contains('template-product')) return;

  function getPayBtn() {
    return document.querySelector('.shopify-payment-button__button') ||
           document.querySelector('.shopify-payment-button') ||
           document.querySelector('.product-form__submit');
  }

  var ticking = false;
  var latched = false;
  function check() {
    ticking = false;
    var atTop = (window.scrollY || window.pageYOffset || 0) < 10;
    if (atTop) {
      // Back at the top of the page — reveal the bubble again.
      latched = false;
    } else {
      var btn = getPayBtn();
      if (btn) {
        var r = btn.getBoundingClientRect();
        var vh = window.innerHeight || document.documentElement.clientHeight;
        // Once the Shop Pay button reaches the bottom band where the bubble
        // sits, latch it hidden — and keep it hidden until we return to the top.
        if (r.bottom > (vh - 150) && r.top < vh) latched = true;
      }
    }
    document.body.classList.toggle('crc-hide-coax', latched);
  }
  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(check);
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
  document.addEventListener('DOMContentLoaded', check);
  window.addEventListener('load', check);
  // Coax loads async — re-check a couple of times after load.
  setTimeout(check, 1500);
  setTimeout(check, 3500);
})();


/* ------------------------------------------------------------------
   Product gallery "L" wrap (desktop): when the vertical thumbnail
   rail would run past the bottom of the hero image, relocate the
   overflow thumbnails into a horizontal row directly under the hero
   (.crc-thumb-overflow). media-gallery.js binds each thumbnail's
   click listener to the element at upgrade time, so moving the <li>
   keeps the hero-swap working. We mirror aria-current on click so the
   active border also shows on relocated thumbnails. Fails safe: any
   error leaves the thumbnails exactly where they are.
   ------------------------------------------------------------------ */
(function () {
  var MQ = '(min-width: 990px) and (hover: hover) and (pointer: fine)';
  var THUMB_GAP = 10;

  function setup(gallery) {
    if (gallery.__crcL) return;
    var slider = gallery.querySelector('[id^="GalleryThumbnails"]');
    var list = slider && slider.querySelector('.thumbnail-list');
    var hero = gallery.querySelector('[id^="GalleryViewer"]');
    if (!list || !hero) return;
    gallery.__crcL = true;

    var n = 0;
    list.querySelectorAll('.thumbnail-list__item').forEach(function (li) {
      if (li.dataset.crcIdx == null) li.dataset.crcIdx = n;
      n++;
    });

    // Mirror aria-current onto the clicked thumb (capture phase, before
    // media-gallery.js) so relocated thumbs show the active border too.
    gallery.addEventListener('click', function (e) {
      var item = e.target.closest('.thumbnail-list__item, .crc-thumb-overflow > li');
      if (!item || !gallery.contains(item)) return;
      gallery.querySelectorAll('.thumbnail-list__item button[aria-current], .crc-thumb-overflow button[aria-current]')
        .forEach(function (b) { b.removeAttribute('aria-current'); });
      var btn = item.querySelector('button');
      if (btn) btn.setAttribute('aria-current', 'true');
    }, true);

    var raf = null;
    gallery.__crcRelayout = function () {
      if (raf) return;
      raf = requestAnimationFrame(function () { raf = null; relayout(gallery); });
    };

    if (window.ResizeObserver) {
      try { new ResizeObserver(function () { gallery.__crcRelayout(); }).observe(hero); } catch (e) {}
    }
    gallery.__crcRelayout();
  }

  function orderedItems(gallery) {
    var arr = Array.prototype.slice.call(
      gallery.querySelectorAll('.thumbnail-list__item, .crc-thumb-overflow > li')
    );
    arr.sort(function (a, b) { return (+a.dataset.crcIdx || 0) - (+b.dataset.crcIdx || 0); });
    return arr;
  }

  function relayout(gallery) {
    try {
      var slider = gallery.querySelector('[id^="GalleryThumbnails"]');
      var list = slider && slider.querySelector('.thumbnail-list');
      var hero = gallery.querySelector('[id^="GalleryViewer"]');
      if (!list || !hero) return;
      var items = orderedItems(gallery);
      if (!items.length) return;
      var overflow = gallery.querySelector('.crc-thumb-overflow');

      // Off the desktop L layout — everything back in the vertical rail.
      if (!window.matchMedia(MQ).matches) {
        items.forEach(function (li) { list.appendChild(li); });
        if (slider) slider.style.maxHeight = '';
        if (overflow && overflow.parentNode) overflow.parentNode.removeChild(overflow);
        return;
      }

      if (items[0].parentElement !== list) list.appendChild(items[0]);
      // The hero box and thumbs are square (aspect-ratio:1/1 / fixed 72px), so
      // WIDTH == height and is stable the moment layout applies — height can read
      // as 0/tiny before the image loads, which previously caused 3-image
      // products to wrongly split. Measure widths.
      var thumbH = items[0].getBoundingClientRect().width || 72;
      var heroH = hero.getBoundingClientRect().width || 0;
      if (heroH < thumbH) return; // layout not ready / implausible — leave as-is

      // Cap the rail at the hero height so a thumbnail that crosses the bottom
      // spills past the photo (overflow:visible) instead of stretching the grid
      // row and pushing the overflow row down. Keeps overflow exactly 16px below.
      if (slider) slider.style.maxHeight = heroH + 'px';

      // Thumbs whose top starts within the hero stay in the rail (the last one
      // may partially cross the bottom); the first thumb that starts below the
      // hero begins the row underneath — a continuous L, not a corner gap.
      var fit = Math.ceil(heroH / (thumbH + THUMB_GAP));
      if (fit < 1) fit = 1;

      // Everything fits beside the hero — no overflow row needed.
      if (fit >= items.length) {
        items.forEach(function (li) { list.appendChild(li); });
        if (overflow && overflow.parentNode) overflow.parentNode.removeChild(overflow);
        return;
      }

      if (!overflow) {
        overflow = document.createElement('ul');
        overflow.className = 'crc-thumb-overflow';
        gallery.appendChild(overflow);
      }
      items.slice(0, fit).forEach(function (li) { list.appendChild(li); });
      items.slice(fit).forEach(function (li) { overflow.appendChild(li); });

      // Pin the overflow's top to the PHOTO's measured bottom + the SAME gap the
      // rail actually renders beside the photo. The rail thumbs sit ~11px from
      // the photo (the 16px column-gap minus the rail box's spare width), so we
      // measure that real side gap and reuse it below — keeps both gaps equal.
      var photo = gallery.querySelector('.product__media-item.is-active .product-media-container');
      var heroBottom = hero.getBoundingClientRect().bottom;
      var photoRect = photo ? photo.getBoundingClientRect() : hero.getBoundingClientRect();
      var photoBottom = photoRect.bottom;
      // Real side gap = photo's left edge minus the last rail thumb's right edge.
      var lastThumb = list.querySelector('.thumbnail-list__item:last-child');
      var sideGap = 16;
      if (lastThumb) {
        var sg = Math.round(photoRect.left - lastThumb.getBoundingClientRect().right);
        if (sg > 0 && sg < 60) sideGap = sg;
      }
      var slack = heroBottom - photoBottom;       // px the box extends past the photo
      // grid row-gap is 16; we want the visible gap to equal sideGap, so adjust.
      overflow.style.marginTop = (-slack) + 'px'; // CSS row-gap (11px) now matches the side gap
    } catch (err) { /* leave thumbnails as-is */ }
  }

  function run() { document.querySelectorAll('media-gallery').forEach(setup); }

  if (document.readyState !== 'loading') run();
  else document.addEventListener('DOMContentLoaded', run);
  window.addEventListener('load', run);
  setTimeout(run, 400);
  setTimeout(run, 1200);

  var rt;
  window.addEventListener('resize', function () {
    clearTimeout(rt);
    rt = setTimeout(function () {
      document.querySelectorAll('media-gallery').forEach(function (g) {
        if (g.__crcRelayout) g.__crcRelayout(); else setup(g);
      });
    }, 150);
  });
})();
